import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { 
  AlertCircle, 
  FileSearch, 
  GraduationCap, 
  AlertTriangle,
  Loader2,
  CheckCircle2,
  XCircle,
  Mic,
  Cloud,
  Sparkles,
  Brain,
  Zap,
  Target,
  Shield,
  Lightbulb,
  ArrowRight,
  ClipboardList,
  TrendingUp,
  BookOpen
} from 'lucide-react';
import axios from 'axios';
import VoiceNoteRecorder from './VoiceNoteRecorderV2';
import WeatherSafetyForecast from './WeatherSafetyForecast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Professional header image
const HEADER_IMAGE_URL = "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1600&q=80";

// Tool configurations with enhanced styling
const TOOL_CONFIG = {
  'risk-assessment': {
    icon: AlertTriangle,
    color: 'from-orange-500 to-red-600',
    bgLight: 'bg-orange-50',
    textColor: 'text-orange-600',
    title: 'Risk Assessment',
    description: 'AI-powered multi-factor risk analysis',
    features: ['Hazard scoring', 'Priority ranking', 'Mitigation strategies']
  },
  'accident-investigation': {
    icon: FileSearch,
    color: 'from-blue-500 to-indigo-600',
    bgLight: 'bg-blue-50',
    textColor: 'text-blue-600',
    title: 'Accident Analysis',
    description: 'Root cause investigation with AI',
    features: ['Cause analysis', 'Contributing factors', 'Prevention plans']
  },
  'training-content': {
    icon: GraduationCap,
    color: 'from-green-500 to-emerald-600',
    bgLight: 'bg-green-50',
    textColor: 'text-green-600',
    title: 'Training Generator',
    description: 'Custom safety training materials',
    features: ['OSHA compliant', 'Interactive content', 'Assessment quizzes']
  },
  'hazard-identification': {
    icon: AlertCircle,
    color: 'from-purple-500 to-violet-600',
    bgLight: 'bg-purple-50',
    textColor: 'text-purple-600',
    title: 'Hazard Identification',
    description: 'Systematic hazard detection',
    features: ['JHA generation', 'Control measures', 'Risk matrices']
  },
  'weather-safety': {
    icon: Cloud,
    color: 'from-cyan-500 to-blue-600',
    bgLight: 'bg-cyan-50',
    textColor: 'text-cyan-600',
    title: 'Weather-Safety',
    description: 'Weather-based safety forecasting',
    features: ['7-day forecast', 'Work advisories', 'PPE recommendations']
  }
};

// Quick action templates for faster AI usage
const QUICK_TEMPLATES = {
  'risk-assessment': [
    { label: 'Construction Site', data: { assessment_title: 'Construction Site Risk Assessment', site_description: 'Active construction site with multiple trades working simultaneously', activities: 'Excavation, Scaffolding, Electrical work, Welding', equipment_present: 'Excavator, Scaffolds, Power tools, Welding equipment' }},
    { label: 'Manufacturing Floor', data: { assessment_title: 'Manufacturing Floor Assessment', site_description: 'Industrial manufacturing facility with heavy machinery', activities: 'Machine operation, Material handling, Assembly, Packaging', equipment_present: 'CNC machines, Forklifts, Conveyor belts, Presses' }},
    { label: 'Warehouse Operations', data: { assessment_title: 'Warehouse Safety Assessment', site_description: 'Large distribution warehouse with high-rack storage', activities: 'Forklift operations, Order picking, Loading/unloading, Inventory management', equipment_present: 'Forklifts, Pallet jacks, Dock levelers, Racking systems' }}
  ],
  'accident-investigation': [
    { label: 'Slip/Trip/Fall', data: { incident_description: 'Worker slipped on wet floor and fell, resulting in injury', location: 'Production floor near cleaning area' }},
    { label: 'Equipment Contact', data: { incident_description: 'Worker contacted moving machinery during operation', location: 'Machine shop' }},
    { label: 'Falling Object', data: { incident_description: 'Object fell from elevated storage and struck worker', location: 'Warehouse storage area' }}
  ],
  'training-content': [
    { label: 'Fall Protection', data: { topic: 'Fall Protection and Prevention', target_audience: 'Construction workers', industry: 'Construction', compliance_standards: 'OSHA 29 CFR 1926.501', specific_hazards: 'Working at heights, ladder safety, scaffolding' }},
    { label: 'Hazard Communication', data: { topic: 'Hazard Communication (HazCom)', target_audience: 'All employees', industry: 'General Industry', compliance_standards: 'OSHA 29 CFR 1910.1200', specific_hazards: 'Chemical exposure, SDS reading, labeling' }},
    { label: 'Lockout/Tagout', data: { topic: 'Lockout/Tagout Procedures', target_audience: 'Maintenance personnel', industry: 'Manufacturing', compliance_standards: 'OSHA 29 CFR 1910.147', specific_hazards: 'Hazardous energy, equipment isolation, verification' }}
  ],
  'hazard-identification': [
    { label: 'Office Environment', data: { site_description: 'Multi-story office building with cubicle workstations', work_activities: 'Computer work, meetings, filing, break room activities', industry_type: 'Office/Administrative' }},
    { label: 'Chemical Storage', data: { site_description: 'Chemical storage and handling facility', work_activities: 'Chemical receiving, storage, dispensing, inventory', industry_type: 'Chemical/Industrial' }},
    { label: 'Outdoor Work', data: { site_description: 'Outdoor work site with varying terrain and weather exposure', work_activities: 'Landscaping, utility work, road maintenance', industry_type: 'Outdoor/Utilities' }}
  ]
};

function AdvancedToolsPage() {
  const [activeTab, setActiveTab] = useState('risk-assessment');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [voiceNotes, setVoiceNotes] = useState([]);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);

  // Risk Assessment State
  const [riskAssessment, setRiskAssessment] = useState({
    assessment_title: '',
    site_description: '',
    activities: '',
    equipment_present: '',
    personnel_count: '',
    environmental_factors: ''
  });

  // Accident Investigation State
  const [accidentInvestigation, setAccidentInvestigation] = useState({
    incident_description: '',
    incident_date: '',
    location: '',
    involved_parties: '',
    witness_statements: '',
    existing_evidence: ''
  });

  // Training Content State
  const [trainingContent, setTrainingContent] = useState({
    topic: '',
    target_audience: '',
    industry: '',
    duration_minutes: 30,
    compliance_standards: '',
    specific_hazards: ''
  });

  // Hazard Identification State
  const [hazardIdentification, setHazardIdentification] = useState({
    site_description: '',
    work_activities: '',
    industry_type: '',
    immediate_assessment: true
  });

  const getToken = () => localStorage.getItem('auth_token');
  const getCompanyId = () => {
    const user = JSON.parse(localStorage.getItem('user_data') || '{}');
    return user.company_id || 'default-company';
  };

  const handleRiskAssessment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const payload = {
        company_id: getCompanyId(),
        assessment_title: riskAssessment.assessment_title,
        site_description: riskAssessment.site_description,
        activities: riskAssessment.activities.split(',').map(s => s.trim()).filter(Boolean),
        equipment_present: riskAssessment.equipment_present.split(',').map(s => s.trim()).filter(Boolean),
        personnel_count: riskAssessment.personnel_count ? parseInt(riskAssessment.personnel_count) : null,
        environmental_factors: riskAssessment.environmental_factors ? JSON.parse(riskAssessment.environmental_factors) : null
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/ai/risk-assessment`,
        payload,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVoiceNote = (voiceNote) => {
    setVoiceNotes(prev => [...prev, voiceNote]);
  };

  const handleAccidentInvestigation = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const payload = {
        company_id: getCompanyId(),
        incident_description: accidentInvestigation.incident_description,
        incident_date: accidentInvestigation.incident_date,
        location: accidentInvestigation.location,
        involved_parties: accidentInvestigation.involved_parties.split(',').map(s => s.trim()).filter(Boolean),
        witness_statements: accidentInvestigation.witness_statements 
          ? accidentInvestigation.witness_statements.split('\n').filter(Boolean) 
          : null,
        existing_evidence: accidentInvestigation.existing_evidence || null,
        voice_notes: voiceNotes  // Include voice notes
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/ai/accident-investigation`,
        payload,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      setResult(response.data);
      // Clear voice notes after successful submission
      setVoiceNotes([]);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTrainingContent = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const payload = {
        company_id: getCompanyId(),
        topic: trainingContent.topic,
        target_audience: trainingContent.target_audience,
        industry: trainingContent.industry,
        duration_minutes: parseInt(trainingContent.duration_minutes) || 30,
        compliance_standards: trainingContent.compliance_standards 
          ? trainingContent.compliance_standards.split(',').map(s => s.trim()).filter(Boolean)
          : null,
        specific_hazards: trainingContent.specific_hazards
          ? trainingContent.specific_hazards.split(',').map(s => s.trim()).filter(Boolean)
          : null
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/ai/training-content`,
        payload,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleHazardIdentification = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const payload = {
        company_id: getCompanyId(),
        site_description: hazardIdentification.site_description,
        work_activities: hazardIdentification.work_activities.split(',').map(s => s.trim()).filter(Boolean),
        industry_type: hazardIdentification.industry_type,
        immediate_assessment: hazardIdentification.immediate_assessment
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/ai/hazard-identification`,
        payload,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stylized Header with Visual */}
      <div className="relative rounded-xl md:rounded-2xl overflow-hidden shadow-xl">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HEADER_IMAGE_URL})` }}
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-purple-900/90 to-indigo-900/85" />
        
        {/* Content */}
        <div className="relative px-4 md:px-8 py-6 md:py-10">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-3 md:mb-4">
                <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                  <Brain className="w-5 h-5 md:w-7 md:h-7 text-white" />
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30 px-2 md:px-3 py-1 text-xs">
                    <Sparkles className="w-3 h-3 mr-1" />
                    AI-Powered
                  </Badge>
                  <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 px-2 md:px-3 py-1 text-xs hidden sm:flex">
                    <Zap className="w-3 h-3 mr-1" />
                    Smart Analysis
                  </Badge>
                </div>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 md:mb-3">
                Advanced AI Safety Tools
              </h1>
              <p className="text-sm md:text-lg text-purple-100/90 max-w-2xl">
                Powerful AI-driven tools for comprehensive safety analysis, risk assessment, training generation, and hazard identification
              </p>
            </div>
            
            {/* Quick Stats */}
            <div className="hidden lg:flex gap-3">
              {Object.entries(TOOL_CONFIG).slice(0, 4).map(([key, config]) => (
                <div 
                  key={key}
                  onClick={() => { setActiveTab(key); setResult(null); setError(null); }}
                  className={`bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20 text-center cursor-pointer hover:bg-white/20 transition-all min-w-[90px] ${activeTab === key ? 'ring-2 ring-white/50' : ''}`}
                >
                  <config.icon className="w-5 h-5 text-white mx-auto mb-1" />
                  <p className="text-xs text-purple-200 font-medium">{config.title}</p>
                </div>
              ))}
            </div>
          </div>
          
          {/* Feature Pills - Scrollable on mobile */}
          <div className="flex flex-nowrap md:flex-wrap gap-2 mt-4 md:mt-6 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
            {[
              { icon: Target, text: 'Risk Scoring' },
              { icon: Shield, text: 'OSHA Compliance' },
              { icon: Lightbulb, text: 'Smart Recommendations' },
              { icon: TrendingUp, text: 'Predictive Analysis' },
              { icon: BookOpen, text: 'Training Generation' },
            ].map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-3 md:px-4 py-1.5 md:py-2 border border-white/20 whitespace-nowrap flex-shrink-0">
                <feature.icon className="w-3 h-3 md:w-4 md:h-4 text-purple-300" />
                <span className="text-xs md:text-sm text-white/90">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tool Selection Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-3">
        {Object.entries(TOOL_CONFIG).map(([key, config]) => (
          <Card 
            key={key}
            onClick={() => { setActiveTab(key); setResult(null); setError(null); }}
            className={`cursor-pointer transition-all hover:shadow-lg hover:-translate-y-1 ${activeTab === key ? 'ring-2 ring-offset-2 ' + config.textColor.replace('text', 'ring') : ''}`}
          >
            <CardContent className="p-4">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${config.color} flex items-center justify-center mb-3`}>
                <config.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 text-sm">{config.title}</h3>
              <p className="text-xs text-gray-500 mt-1">{config.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Templates Section */}
      {QUICK_TEMPLATES[activeTab] && (
        <Card className="border-dashed">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500" />
              Quick Start Templates
            </CardTitle>
            <CardDescription className="text-xs">
              Click a template to auto-fill the form with common scenarios
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-2">
              {QUICK_TEMPLATES[activeTab].map((template, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (activeTab === 'risk-assessment') {
                      setRiskAssessment(prev => ({ ...prev, ...template.data }));
                    } else if (activeTab === 'accident-investigation') {
                      setAccidentInvestigation(prev => ({ ...prev, ...template.data }));
                    } else if (activeTab === 'training-content') {
                      setTrainingContent(prev => ({ ...prev, ...template.data }));
                    } else if (activeTab === 'hazard-identification') {
                      setHazardIdentification(prev => ({ ...prev, ...template.data }));
                    }
                  }}
                  className="text-xs"
                >
                  <ClipboardList className="w-3 h-3 mr-1" />
                  {template.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Risk Assessment Tab */}
      {activeTab === 'risk-assessment' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="shadow-lg border-t-4 border-t-orange-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <span>Risk Assessment</span>
                  <p className="text-sm font-normal text-gray-500">Multi-factor AI analysis</p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRiskAssessment} className="space-y-4">
                <div>
                  <Label htmlFor="assessment_title">Assessment Title</Label>
                  <Input
                    id="assessment_title"
                    value={riskAssessment.assessment_title}
                    onChange={(e) => setRiskAssessment({...riskAssessment, assessment_title: e.target.value})}
                    placeholder="e.g., Construction Site - Building A"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="site_description">Site Description</Label>
                  <Textarea
                    id="site_description"
                    value={riskAssessment.site_description}
                    onChange={(e) => setRiskAssessment({...riskAssessment, site_description: e.target.value})}
                    placeholder="Describe the workplace environment..."
                    rows={3}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="activities">Activities (comma-separated)</Label>
                  <Input
                    id="activities"
                    value={riskAssessment.activities}
                    onChange={(e) => setRiskAssessment({...riskAssessment, activities: e.target.value})}
                    placeholder="e.g., Welding, Scaffolding, Electrical work"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="equipment_present">Equipment Present (comma-separated)</Label>
                  <Input
                    id="equipment_present"
                    value={riskAssessment.equipment_present}
                    onChange={(e) => setRiskAssessment({...riskAssessment, equipment_present: e.target.value})}
                    placeholder="e.g., Crane, Power tools, Scaffolds"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="personnel_count">Personnel Count</Label>
                  <Input
                    id="personnel_count"
                    type="number"
                    value={riskAssessment.personnel_count}
                    onChange={(e) => setRiskAssessment({...riskAssessment, personnel_count: e.target.value})}
                    placeholder="Number of workers"
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="flex-1 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700" disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                    Generate AI Assessment
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowVoiceRecorder(true)}>
                    <Mic className="h-4 w-4" />
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <ResultDisplay result={result} error={error} type="risk-assessment" />
        </div>
      )}

      {/* Accident Investigation Tab */}
      {activeTab === 'accident-investigation' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="shadow-lg border-t-4 border-t-blue-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <FileSearch className="h-5 w-5 text-white" />
                </div>
                <div>
                  <span>Accident Investigation</span>
                  <p className="text-sm font-normal text-gray-500">Root cause AI analysis</p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAccidentInvestigation} className="space-y-4">
                <div>
                  <Label htmlFor="incident_description">Incident Description</Label>
                  <Textarea
                    id="incident_description"
                    value={accidentInvestigation.incident_description}
                    onChange={(e) => setAccidentInvestigation({...accidentInvestigation, incident_description: e.target.value})}
                    placeholder="Describe what happened..."
                    rows={3}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="incident_date">Incident Date</Label>
                    <Input
                      id="incident_date"
                      type="date"
                      value={accidentInvestigation.incident_date}
                      onChange={(e) => setAccidentInvestigation({...accidentInvestigation, incident_date: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={accidentInvestigation.location}
                      onChange={(e) => setAccidentInvestigation({...accidentInvestigation, location: e.target.value})}
                      placeholder="Building, floor, room"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="involved_parties">Involved Parties (comma-separated)</Label>
                  <Input
                    id="involved_parties"
                    value={accidentInvestigation.involved_parties}
                    onChange={(e) => setAccidentInvestigation({...accidentInvestigation, involved_parties: e.target.value})}
                    placeholder="Names and roles"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="witness_statements">Witness Statements (one per line)</Label>
                  <Textarea
                    id="witness_statements"
                    value={accidentInvestigation.witness_statements}
                    onChange={(e) => setAccidentInvestigation({...accidentInvestigation, witness_statements: e.target.value})}
                    placeholder="Enter witness statements..."
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="existing_evidence">Existing Evidence</Label>
                    <Input
                      id="existing_evidence"
                      value={accidentInvestigation.existing_evidence}
                      onChange={(e) => setAccidentInvestigation({...accidentInvestigation, existing_evidence: e.target.value})}
                      placeholder="Photos, documents, measurements..."
                    />
                  </div>

                  {/* Voice Notes Display */}
                  {voiceNotes.length > 0 && (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Mic className="h-4 w-4" />
                        Voice Notes ({voiceNotes.length})
                      </p>
                      <div className="space-y-1 max-h-24 overflow-y-auto">
                        {voiceNotes.map((note, idx) => (
                          <div key={note.id} className="text-xs bg-white p-2 rounded">
                            {note.transcription.substring(0, 80)}...
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      type="button"
                      variant="outline"
                      onClick={() => setShowVoiceRecorder(true)}
                      disabled={loading}
                    >
                      <Mic className="h-4 w-4 mr-2" />
                      Add Voice Note
                    </Button>
                    <Button type="submit" disabled={loading}>
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Analyze Accident
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <ResultDisplay result={result} error={error} type="accident-investigation" />
          </div>
      )}

      {/* Training Content Tab */}
      {activeTab === 'training-content' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="shadow-lg border-t-4 border-t-green-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                  <GraduationCap className="h-5 w-5 text-white" />
                </div>
                <div>
                  <span>Training Generator</span>
                  <p className="text-sm font-normal text-gray-500">Custom safety materials</p>
                </div>
              </CardTitle>
            </CardHeader>
              <CardContent>
                <form onSubmit={handleTrainingContent} className="space-y-4">
                  <div>
                    <Label htmlFor="topic">Training Topic</Label>
                    <Input
                      id="topic"
                      value={trainingContent.topic}
                      onChange={(e) => setTrainingContent({...trainingContent, topic: e.target.value})}
                      placeholder="e.g., Fall Protection and Prevention"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="target_audience">Target Audience</Label>
                    <Input
                      id="target_audience"
                      value={trainingContent.target_audience}
                      onChange={(e) => setTrainingContent({...trainingContent, target_audience: e.target.value})}
                      placeholder="e.g., Construction workers and supervisors"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="industry">Industry</Label>
                      <Input
                        id="industry"
                        value={trainingContent.industry}
                        onChange={(e) => setTrainingContent({...trainingContent, industry: e.target.value})}
                        placeholder="e.g., Construction"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="duration_minutes">Duration (minutes)</Label>
                      <Input
                        id="duration_minutes"
                        type="number"
                        value={trainingContent.duration_minutes}
                        onChange={(e) => setTrainingContent({...trainingContent, duration_minutes: e.target.value})}
                        placeholder="30"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="compliance_standards">Compliance Standards (comma-separated)</Label>
                    <Input
                      id="compliance_standards"
                      value={trainingContent.compliance_standards}
                      onChange={(e) => setTrainingContent({...trainingContent, compliance_standards: e.target.value})}
                      placeholder="e.g., 29 CFR 1926.501, 29 CFR 1926.502"
                    />
                  </div>

                  <div>
                    <Label htmlFor="specific_hazards">Specific Hazards (comma-separated)</Label>
                    <Input
                      id="specific_hazards"
                      value={trainingContent.specific_hazards}
                      onChange={(e) => setTrainingContent({...trainingContent, specific_hazards: e.target.value})}
                      placeholder="e.g., Working at heights, Unprotected edges"
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Generate Training Content
                  </Button>
                </form>
              </CardContent>
            </Card>

            <ResultDisplay result={result} error={error} type="training-content" />
          </div>
      )}

      {/* Hazard Identification Tab */}
      {activeTab === 'hazard-identification' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="shadow-lg border-t-4 border-t-purple-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <span>Hazard Identification</span>
                  <p className="text-sm font-normal text-gray-500">Systematic hazard detection</p>
                </div>
              </CardTitle>
            </CardHeader>
              <CardContent>
                <form onSubmit={handleHazardIdentification} className="space-y-4">
                  <div>
                    <Label htmlFor="site_description_hazard">Site Description</Label>
                    <Textarea
                      id="site_description_hazard"
                      value={hazardIdentification.site_description}
                      onChange={(e) => setHazardIdentification({...hazardIdentification, site_description: e.target.value})}
                      placeholder="Describe the work site in detail..."
                      rows={4}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="work_activities">Work Activities (comma-separated)</Label>
                    <Input
                      id="work_activities"
                      value={hazardIdentification.work_activities}
                      onChange={(e) => setHazardIdentification({...hazardIdentification, work_activities: e.target.value})}
                      placeholder="e.g., Forklift operation, Material handling"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="industry_type">Industry Type</Label>
                    <Input
                      id="industry_type"
                      value={hazardIdentification.industry_type}
                      onChange={(e) => setHazardIdentification({...hazardIdentification, industry_type: e.target.value})}
                      placeholder="e.g., Warehousing and Distribution"
                      required
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="immediate_assessment"
                      checked={hazardIdentification.immediate_assessment}
                      onChange={(e) => setHazardIdentification({...hazardIdentification, immediate_assessment: e.target.checked})}
                      className="rounded"
                    />
                    <Label htmlFor="immediate_assessment" className="cursor-pointer">
                      Immediate Assessment Required
                    </Label>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Identify Hazards
                  </Button>
                </form>
              </CardContent>
            </Card>

            <ResultDisplay result={result} error={error} type="hazard-identification" />
          </div>
      )}

      {/* Weather-Safety Forecast Tab */}
      {activeTab === 'weather-safety' && (
        <WeatherSafetyForecast />
      )}
      {/* Voice Note Recorder Modal */}
      <VoiceNoteRecorder
        isOpen={showVoiceRecorder}
        onSave={handleAddVoiceNote}
        existingNotes={voiceNotes}
        onClose={() => setShowVoiceRecorder(false)}
      />
    </div>
  );
}

// Result Display Component
function ResultDisplay({ result, error, type }) {
  if (error) {
    return (
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <XCircle className="h-5 w-5" />
            Error
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!result) {
    return (
      <Card className="border-gray-200">
        <CardHeader>
          <CardTitle>Results</CardTitle>
          <CardDescription>Submit the form to see AI-generated results</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-64 text-gray-400">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-2" />
            <p>Awaiting analysis...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-green-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-green-600">
          <CheckCircle2 className="h-5 w-5" />
          Analysis Complete
        </CardTitle>
        <CardDescription>ID: {result.id}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 max-h-[600px] overflow-y-auto">
        {type === 'risk-assessment' && <RiskAssessmentResult data={result} />}
        {type === 'accident-investigation' && <AccidentInvestigationResult data={result} />}
        {type === 'training-content' && <TrainingContentResult data={result} />}
        {type === 'hazard-identification' && <HazardIdentificationResult data={result} />}
      </CardContent>
    </Card>
  );
}

// Individual Result Components
function RiskAssessmentResult({ data }) {
  return (
    <div className="space-y-4">
      <div className="p-3 bg-orange-50 rounded-lg">
        <div className="flex items-center justify-between">
          <span className="font-semibold">Risk Level:</span>
          <span className={`px-3 py-1 rounded-full text-sm font-bold ${
            data.risk_level === 'critical' ? 'bg-red-600 text-white' :
            data.risk_level === 'high' ? 'bg-orange-500 text-white' :
            data.risk_level === 'moderate' ? 'bg-yellow-500 text-white' :
            'bg-green-500 text-white'
          }`}>
            {data.risk_level?.toUpperCase()}
          </span>
        </div>
        <div className="mt-2">
          <span className="font-semibold">Overall Risk Score:</span>
          <span className="ml-2 text-lg font-bold">{data.overall_risk_score?.toFixed(1)}/25</span>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-sm mb-2">Identified Hazards ({data.identified_hazards?.length || 0})</h4>
        <div className="space-y-2">
          {data.identified_hazards?.slice(0, 3).map((hazard, idx) => (
            <div key={idx} className="p-2 bg-gray-50 rounded text-sm">
              <span className="font-medium">{hazard.hazard_name}</span>
              {hazard.risk_score && (
                <span className="ml-2 text-xs bg-orange-200 px-2 py-1 rounded">
                  Risk: {hazard.risk_score}/25
                </span>
              )}
            </div>
          ))}
          {data.identified_hazards?.length > 3 && (
            <p className="text-xs text-gray-500">+ {data.identified_hazards.length - 3} more hazards</p>
          )}
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-sm mb-2">Priority Actions</h4>
        <ul className="list-disc list-inside space-y-1 text-sm">
          {data.priority_actions?.slice(0, 3).map((action, idx) => (
            <li key={idx} className="text-gray-700">{action}</li>
          ))}
        </ul>
      </div>

      {data.estimated_cost && (
        <div className="p-2 bg-blue-50 rounded">
          <span className="font-semibold text-sm">Est. Cost:</span>
          <span className="ml-2">${data.estimated_cost?.toLocaleString()}</span>
        </div>
      )}
    </div>
  );
}

function AccidentInvestigationResult({ data }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        <div className="p-2 bg-red-50 rounded">
          <p className="text-xs text-gray-600">Severity</p>
          <p className="font-bold text-sm capitalize">{data.severity_assessment}</p>
        </div>
        <div className="p-2 bg-yellow-50 rounded">
          <p className="text-xs text-gray-600">Recurrence Risk</p>
          <p className="font-bold text-sm capitalize">{data.likelihood_of_recurrence}</p>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-sm mb-2">Root Cause Analysis</h4>
        <div className="p-2 bg-gray-50 rounded text-sm space-y-1">
          <p><span className="font-medium">Immediate:</span> {data.root_cause_analysis?.immediate_cause}</p>
          <p><span className="font-medium">Root:</span> {data.root_cause_analysis?.root_cause}</p>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-sm mb-2">Contributing Factors ({data.contributing_factors?.length || 0})</h4>
        <ul className="list-disc list-inside space-y-1 text-sm">
          {data.contributing_factors?.slice(0, 3).map((factor, idx) => (
            <li key={idx}>{factor}</li>
          ))}
        </ul>
      </div>

      {data.osha_violations?.length > 0 && (
        <div className="p-2 bg-red-50 rounded">
          <h4 className="font-semibold text-sm mb-2">OSHA Violations ({data.osha_violations.length})</h4>
          {data.osha_violations.slice(0, 2).map((violation, idx) => (
            <div key={idx} className="text-xs mb-1">
              <p className="font-medium">{violation.standard}</p>
              <p className="text-gray-600">{violation.description}</p>
            </div>
          ))}
        </div>
      )}

      <div>
        <h4 className="font-semibold text-sm mb-2">Corrective Actions ({data.corrective_actions?.length || 0})</h4>
        <div className="space-y-1">
          {data.corrective_actions?.slice(0, 3).map((action, idx) => (
            <div key={idx} className="p-2 bg-green-50 rounded text-xs">
              <p className="font-medium">{action.action}</p>
              <p className="text-gray-600">Priority: {action.priority}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TrainingContentResult({ data }) {
  return (
    <div className="space-y-4">
      <div className="p-3 bg-blue-50 rounded-lg">
        <h3 className="font-bold">{data.topic}</h3>
        <p className="text-sm text-gray-600">{data.target_audience}</p>
        <p className="text-sm mt-1">Duration: {data.duration_minutes} minutes</p>
      </div>

      <div>
        <h4 className="font-semibold text-sm mb-2">Learning Objectives ({data.learning_objectives?.length || 0})</h4>
        <ul className="list-disc list-inside space-y-1 text-sm">
          {data.learning_objectives?.map((obj, idx) => (
            <li key={idx}>{obj}</li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="font-semibold text-sm mb-2">Content Modules ({data.content_outline?.length || 0})</h4>
        <div className="space-y-2">
          {data.content_outline?.slice(0, 3).map((module, idx) => (
            <div key={idx} className="p-2 bg-gray-50 rounded text-sm">
              <p className="font-medium">{module.module_title}</p>
              <p className="text-xs text-gray-600">{module.duration_minutes} min</p>
            </div>
          ))}
          {data.content_outline?.length > 3 && (
            <p className="text-xs text-gray-500">+ {data.content_outline.length - 3} more modules</p>
          )}
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-sm mb-2">Key Points</h4>
        <ul className="list-disc list-inside space-y-1 text-sm">
          {data.key_points?.slice(0, 5).map((point, idx) => (
            <li key={idx}>{point}</li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="font-semibold text-sm mb-2">Practical Exercises ({data.practical_exercises?.length || 0})</h4>
        <div className="space-y-1">
          {data.practical_exercises?.map((exercise, idx) => (
            <div key={idx} className="p-2 bg-green-50 rounded text-xs">
              <p className="font-medium">{exercise.exercise_name}</p>
              <p className="text-gray-600">{exercise.duration_minutes} min</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-sm mb-2">Assessment ({data.assessment_questions?.length || 0} questions)</h4>
        <p className="text-xs text-gray-600">Includes knowledge checks and scenario-based questions</p>
      </div>
    </div>
  );
}

function HazardIdentificationResult({ data }) {
  const matrix = data.risk_priority_matrix || {};
  
  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-semibold text-sm mb-2">Risk Priority Matrix</h4>
        <div className="grid grid-cols-2 gap-2">
          <div className="p-2 bg-red-50 rounded text-center">
            <p className="text-xs text-gray-600">Critical</p>
            <p className="text-2xl font-bold text-red-600">{matrix.critical?.length || 0}</p>
          </div>
          <div className="p-2 bg-orange-50 rounded text-center">
            <p className="text-xs text-gray-600">High</p>
            <p className="text-2xl font-bold text-orange-600">{matrix.high?.length || 0}</p>
          </div>
          <div className="p-2 bg-yellow-50 rounded text-center">
            <p className="text-xs text-gray-600">Medium</p>
            <p className="text-2xl font-bold text-yellow-600">{matrix.medium?.length || 0}</p>
          </div>
          <div className="p-2 bg-green-50 rounded text-center">
            <p className="text-xs text-gray-600">Low</p>
            <p className="text-2xl font-bold text-green-600">{matrix.low?.length || 0}</p>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-sm mb-2">Identified Hazards ({data.identified_hazards?.length || 0})</h4>
        <div className="space-y-2">
          {data.identified_hazards?.slice(0, 3).map((hazard, idx) => (
            <div key={idx} className="p-2 bg-gray-50 rounded text-sm">
              <p className="font-medium">{hazard.hazard_name}</p>
              <p className="text-xs text-gray-600">{hazard.description}</p>
              <div className="flex gap-2 mt-1">
                <span className="text-xs bg-red-100 px-2 py-0.5 rounded">
                  Severity: {hazard.severity}/5
                </span>
                <span className="text-xs bg-yellow-100 px-2 py-0.5 rounded capitalize">
                  {hazard.likelihood}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {data.immediate_risks?.length > 0 && (
        <div className="p-2 bg-red-50 rounded">
          <h4 className="font-semibold text-sm mb-2">⚠️ Immediate Risks</h4>
          {data.immediate_risks.slice(0, 2).map((risk, idx) => (
            <div key={idx} className="text-xs mb-2">
              <p className="font-medium">{risk.risk_description}</p>
              <p className="text-gray-600">Action: {risk.immediate_action}</p>
            </div>
          ))}
        </div>
      )}

      <div>
        <h4 className="font-semibold text-sm mb-2">Recommended Controls ({data.recommended_controls?.length || 0})</h4>
        <div className="space-y-1">
          {data.recommended_controls?.slice(0, 3).map((control, idx) => (
            <div key={idx} className="p-2 bg-blue-50 rounded text-xs">
              <p className="font-medium capitalize">{control.control_type}</p>
              <p className="text-gray-600">{control.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-sm mb-2">PPE Requirements</h4>
        <ul className="list-disc list-inside space-y-1 text-sm">
          {data.ppe_requirements?.map((ppe, idx) => (
            <li key={idx}>{ppe}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Add VoiceNoteRecorder outside the result component but inside AdvancedToolsPage
// (Need to update the main return to include it)

export default AdvancedToolsPage;
